import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { CreditCard, Video, Calendar, MessageSquare, Mail, LayoutGrid, LogIn, CheckCircle, XCircle, Settings } from "lucide-react";
import api from "../../lib/api";


const iconMap = { "credit-card": CreditCard, video: Video, calendar: Calendar, "message-square": MessageSquare, mail: Mail, "layout-grid": LayoutGrid, "log-in": LogIn };
const statusColors = { configured: { bg: "#D4F2E7", color: "#0a6040", label: "Configured" }, not_configured: { bg: "#FDE8E7", color: "#9e2920", label: "Not Configured" }, partial: { bg: "#FEF6E4", color: "#7c4a00", label: "Partial" } };

const IntegrationsPage = () => {
  const { token } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/integrations");
      setIntegrations(res.data.integrations || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selected = integrations.find(i => i.id === selectedId);

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>;

  const configured = integrations.filter(i => i.status === "configured").length;

  return (
    <AdminLayout title="Integrations" variant="super_admin">
    <div className="p-6 space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure third-party service connections for the platform</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #1B4F8A" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Total Integrations</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{integrations.length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #0E8A55" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Configured</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{configured}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #F5841F" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Pending Setup</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{integrations.length - configured}</p>
        </div>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {integrations.map(intg => {
          const Icon = iconMap[intg.icon] || Settings;
          const sc = statusColors[intg.status] || statusColors.not_configured;

          return (
            <div key={intg.id} className={`bg-background border rounded-xl p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${selectedId === intg.id ? 'ring-2 ring-orange-400' : ''}`}
              onClick={() => setSelectedId(selectedId === intg.id ? null : intg.id)} data-testid={`integration-card-${intg.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-muted/60 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">{intg.name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">{intg.category}</p>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase" style={{ background: sc.bg, color: sc.color }}>
                  {sc.label}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-3">{intg.description}</p>
              {intg.status === "configured" ? (
                <div className="flex items-center gap-1 mt-3 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Connected and active</div>
              ) : (
                <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground"><XCircle className="w-3.5 h-3.5" /> Requires API keys to configure</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="border rounded-xl p-5 bg-background shadow-sm" data-testid="integration-detail">
          <h3 className="font-bold text-lg mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>{selected.name} Configuration</h3>
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="mb-2">To configure {selected.name}, you'll need to provide the following credentials:</p>
            {selected.provider === "stripe" && <ul className="list-disc ml-5 space-y-1"><li>Stripe Secret Key</li><li>Stripe Publishable Key</li><li>Stripe Webhook Secret</li></ul>}
            {selected.provider === "zoom" && <ul className="list-disc ml-5 space-y-1"><li>Zoom OAuth Client ID</li><li>Zoom OAuth Client Secret</li></ul>}
            {selected.provider === "google_calendar" && <ul className="list-disc ml-5 space-y-1"><li>Google Calendar Client ID</li><li>Google Calendar Client Secret</li></ul>}
            {selected.provider === "twilio" && <ul className="list-disc ml-5 space-y-1"><li>Twilio Account SID</li><li>Twilio Auth Token</li><li>Twilio From Number</li></ul>}
            {selected.provider === "aws_ses" && <ul className="list-disc ml-5 space-y-1"><li>AWS Access Key ID</li><li>AWS Secret Access Key</li><li>SES From Email</li></ul>}
            {selected.provider === "microsoft_graph" && <ul className="list-disc ml-5 space-y-1"><li>Microsoft Graph Client ID</li><li>Microsoft Graph Client Secret</li></ul>}
            {selected.provider === "google_oauth" && <p className="text-green-700 font-medium">Google OAuth is configured via Odesie platform and is active.</p>}
            <p className="mt-3 text-xs italic">Configuration is managed through environment variables and platform settings.</p>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default IntegrationsPage;
